import { Formik } from 'formik';

import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { OrgManager, OrgManagerRole } from '@uplift/api';
import { EMPTY_STRING } from '@uplift/constants';
import { useHigherEdManagersQuery } from '@uplift/queries';
import {
  CancelButton, FormModal, ModalActions, ModalContent, Select, SubmitButton
} from '@uplift/ui-v2';

import { managerRoleOptions, transformDataForSelectOptions } from '../helpers';
import { TransferOwnershipFormValues } from '../types';
import { transferOwnershipValidation } from '../validation';

export type TransferOwnershipModalProps = {
  manager: OrgManager;
  onCancel: () => void;
  onSubmit: (values: TransferOwnershipFormValues) => void;
  isCareApp: boolean;
  open: boolean;
};

export const TransferOwnershipModal: React.VFC<TransferOwnershipModalProps> = ({ manager, open, onCancel, onSubmit, isCareApp }) => {
  const { classes } = useTransferOwnershipModalStyles();
  const currentOwnerFullName = `${manager?.user?.firstName} ${manager?.user?.lastName}`;
  const { data: managers } = useHigherEdManagersQuery(manager.orgId);
  const managerSelectOptions = transformDataForSelectOptions(managers);

  const modalContent = isCareApp ? (
    <>
      <Typography variant="body1">
    You are the main point of contact to manage settings, team member access, and student referrals.
      </Typography>
      <Typography variant="body1">
   Ownership can be transferred to another active manager. All managers will receive a notification via email about this change and owner permissions will be transferred immediately.
      </Typography>
    </>
  ) : (
    <>
      <Typography variant="body1">
    The organization owner is the main point of contact to manage settings, team member access, and student referrals. You can transfer ownership to another active manager.
      </Typography>
      <Typography variant="body1">
    All managers will receive a notification via email about this change and owner permissions will be transferred immediately.
      </Typography>
    </>
  );

  const initialValues: TransferOwnershipFormValues = {
    newOwnerId: EMPTY_STRING,
    prevOwnerId: manager.user.id,
    prevOwnerNewRole: OrgManagerRole.admin,
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={transferOwnershipValidation}
      validateOnMount
    >
      {formik => (
        <FormModal
          title="Transfer Ownership"
          onClose={onCancel}
          maxWidth="md"
          open={open}
          data-testid="transfer-ownership-modal"
        >
          <Divider />
          <Box width="600px">
            <ModalContent>
              <Box display="flex" flexDirection="column" mb={3} mt={-3}>
                <Box display="flex" flexDirection="column" gap="16px">
                  {modalContent}
                </Box>
                <Select
                  name="newOwnerId"
                  label="New Organization Owner"
                  items={managerSelectOptions}
                  className={classes.input}
                  placeholder="Select a manager"
                  testId="new-org-owner-select"
                />
                <Select
                  name="prevOwnerNewRole"
                  label={`Select a New Role for ${isCareApp ? 'Yourself' : currentOwnerFullName}`}
                  items={managerRoleOptions}
                  className={classes.input}
                  testId="new-role-for-org-owner-select"
                />
              </Box>
            </ModalContent>
            <Divider />
            <ModalActions>
              <CancelButton variant="text" color="secondary" onClick={onCancel} >
                Cancel
              </CancelButton>
              <SubmitButton
                variant="contained" color="secondary"
                loading={formik.isSubmitting}
                data-testid="transfer-ownership-submit-button"
              >
                Transfer ownership
              </SubmitButton>
            </ModalActions>
          </Box>
        </FormModal>
      )}
    </Formik>
  );
};

const useTransferOwnershipModalStyles = makeStyles({ name: 'TransferOwnershipModal' })((theme) => ({
  input: {
    marginTop: theme.spacing(2),
    '& label.Mui-focused:not(.Mui-error)': {
      color: theme.palette.secondary.main,
    },
    '& div.Mui-focused:not(.Mui-error) fieldset': {
      borderColor: `${theme.palette.secondary.main} !important`,
    },
    '& input': {
      padding: theme.spacing(2, 1.5, 2, 1.5),
    },
    '& span': {
      textTransform: 'capitalize',
    }
  },
  '.MuiListItem-button': {
    textTransform: 'capitalize',
  }
}));
